// src/features/executive/components/DrillDrawer/DrillExportContext.tsx
// Context bridge: drill bodies register their current (drillId, data) so the
// drawer header's Export Excel button knows what to export.

import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { DrillExportData } from 'features/executive/export/use-export-excel'

interface DrillExportContextValue {
  payload: DrillExportData | null
  widgetKey: string | null
  widgetTitle: string | null
  register: (payload: DrillExportData, widgetKey: string, widgetTitle: string) => void
  clear: () => void
}

const DrillExportContext = createContext<DrillExportContextValue | null>(null)

export function DrillExportProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<DrillExportData | null>(null)
  const [widgetKey, setWidgetKey] = useState<string | null>(null)
  const [widgetTitle, setWidgetTitle] = useState<string | null>(null)

  const register = useCallback(
    (p: DrillExportData, k: string, title: string) => {
      setPayload(p)
      setWidgetKey(k)
      setWidgetTitle(title)
    },
    []
  )
  const clear = useCallback(() => {
    setPayload(null)
    setWidgetKey(null)
    setWidgetTitle(null)
  }, [])

  return (
    <DrillExportContext.Provider value={{ payload, widgetKey, widgetTitle, register, clear }}>
      {children}
    </DrillExportContext.Provider>
  )
}

export function useDrillExportContext(): DrillExportContextValue {
  const ctx = useContext(DrillExportContext)
  if (!ctx) throw new Error('useDrillExportContext must be used inside DrillExportProvider')
  return ctx
}

/**
 * Hook used inside drill-body components to register the current data for
 * export. Automatically clears on unmount.
 */
export function useRegisterDrillExport(
  payload: DrillExportData | null,
  widgetKey: string,
  widgetTitle: string
) {
  const { register, clear } = useDrillExportContext()
  useEffect(() => {
    if (payload) register(payload, widgetKey, widgetTitle)
  }, [payload, widgetKey, widgetTitle, register])

  useEffect(() => {
    return () => { clear() }
  }, [clear])
}
